/**
 * Intercepts cart checkout clicks and offers charms before /checkout.
 * Image / title open the charm product page. Add adds to cart and stays
 * in the modal so more than one charm can be attached. Skip continues.
 * @typedef {{productId:number,variantId:number,title:string,variantTitle?:string,price:number,priceFormatted:string,salePrice?:number,salePriceFormatted?:string,url:string,image:string|null,imageAlt:string}} CharmOffer
 */

const ELIGIBILITY_TIMEOUT_MS = 2000;
const OFFER_STORAGE_KEY = 'kb-charm-checkout-offer-ends';

class CharmsCheckoutUpsell extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {CharmOffer[]} */
  #charms = [];

  /** @type {{add:string,adding:string,added:string}} */
  #labels = { add: 'Add', adding: 'Adding…', added: 'Added' };

  /** @type {boolean} */
  #busy = false;

  /** @type {boolean} */
  #proceeding = false;

  /** @type {boolean} */
  #listening = false;

  /** @type {ReturnType<typeof setInterval> | null} */
  #timerId = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog.charms-upsell__dialog');
    this.#charms = this.#parseCharms();
    this.#labels = this.#parseLabels();

    if (!this.#dialog || this.#charms.length === 0) {
      console.warn('[charms-upsell] missing dialog or charms', {
        dialog: Boolean(this.#dialog),
        charms: this.#charms.length,
      });
      return;
    }

    this.#renderCards();
    this.removeAttribute('hidden');
    this.#bindUi();
    this.#bindCheckoutIntercept();
  }

  disconnectedCallback() {
    this.#unbindCheckoutIntercept();
    this.#stopTimer();
  }

  #bindUi() {
    if (!this.#dialog) return;
    this.#dialog.addEventListener('cancel', this.#onDialogCancel);
    this.querySelector('[data-upsell-close]')?.addEventListener('click', this.#onSkip);
    this.querySelector('[data-upsell-skip]')?.addEventListener('click', this.#onSkip);
    this.#dialog.addEventListener('click', this.#onBackdropClick);
  }

  #bindCheckoutIntercept() {
    if (this.#listening) return;
    this.#listening = true;
    document.addEventListener('click', this.#onDocumentClick, true);
    document.addEventListener('submit', this.#onDocumentSubmit, true);
  }

  #unbindCheckoutIntercept() {
    if (!this.#listening) return;
    this.#listening = false;
    document.removeEventListener('click', this.#onDocumentClick, true);
    document.removeEventListener('submit', this.#onDocumentSubmit, true);
  }

  /** @returns {CharmOffer[]} */
  #parseCharms() {
    try {
      const node = this.querySelector('[data-charms-json]');
      if (!node) return [];

      let raw = '';
      if (node instanceof HTMLTemplateElement) {
        raw = node.innerHTML.trim() || node.content.textContent?.trim() || '';
      } else {
        raw = node.textContent?.trim() || '';
      }

      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[charms-upsell] bad charms json', error);
      return [];
    }
  }

  #parseLabels() {
    try {
      const raw = this.querySelector('[data-upsell-labels]')?.textContent?.trim();
      if (!raw) return this.#labels;
      return { ...this.#labels, ...JSON.parse(raw) };
    } catch {
      return this.#labels;
    }
  }

  #renderCards() {
    const grid = this.querySelector('[data-upsell-grid]');
    const template = this.querySelector('template[data-upsell-card]');
    if (!(grid instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    grid.replaceChildren();

    for (const charm of this.#charms) {
      const node = /** @type {HTMLElement | null} */ (template.content.firstElementChild?.cloneNode(true));
      if (!(node instanceof HTMLElement)) continue;

      node.dataset.productId = String(charm.productId);
      node.dataset.variantId = String(charm.variantId);

      const href = charm.url || '';
      const link = node.querySelector('[data-card-link]');
      if (link instanceof HTMLAnchorElement) {
        link.href = href;
      }

      const img = node.querySelector('[data-card-image]');
      if (img instanceof HTMLImageElement) {
        if (charm.image) {
          img.src = charm.image.startsWith('//') ? `https:${charm.image}` : charm.image;
          img.alt = charm.imageAlt || charm.title;
        } else {
          img.remove();
        }
      }

      const title = node.querySelector('[data-card-title]');
      if (title instanceof HTMLAnchorElement) {
        title.href = href;
        title.textContent = charm.title;
      } else if (title) {
        title.textContent = charm.title;
      }

      const price = node.querySelector('[data-card-price]');
      const salePrice = node.querySelector('[data-card-sale-price]');
      const variantLabel =
        charm.variantTitle && charm.variantTitle !== 'Default Title' ? `${charm.variantTitle} · ` : '';
      if (salePrice) {
        salePrice.textContent = `${variantLabel}${charm.salePriceFormatted || charm.priceFormatted}`;
        if (price) price.textContent = charm.priceFormatted || '';
      } else if (price) {
        price.textContent = `${variantLabel}${charm.priceFormatted}`;
      }

      const addBtn = node.querySelector('[data-card-add]');
      if (addBtn instanceof HTMLButtonElement) {
        addBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          void this.#onAdd(charm, addBtn);
        });
      }

      grid.appendChild(node);
    }
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #findCheckoutButton(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    for (const node of path) {
      if (!(node instanceof HTMLElement)) continue;
      if (
        node.matches?.(
          '#checkout, .cart__checkout-button, button[name="checkout"], [name="checkout"]'
        )
      ) {
        return node;
      }
    }

    const target = event.target;
    if (!(target instanceof Element)) return null;
    return target.closest('#checkout, .cart__checkout-button, button[name="checkout"], [name="checkout"]');
  }

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (this.#proceeding || this.#busy) return;
    if (this.#dialog?.open) return;

    const checkoutButton = this.#findCheckoutButton(event);
    if (!(checkoutButton instanceof HTMLElement)) return;
    if (checkoutButton.hasAttribute('disabled')) return;
    if (checkoutButton.closest('charms-checkout-upsell')) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    void this.#openForCheckout();
  };

  /**
   * @param {SubmitEvent} event
   */
  #onDocumentSubmit = (event) => {
    if (this.#proceeding || this.#busy) return;
    if (this.#dialog?.open) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const isCartForm =
      form.id === 'cart-form' ||
      form.getAttribute('action')?.includes('/cart') === true ||
      Boolean(form.querySelector('[name="checkout"]'));

    if (!isCartForm) return;

    const submitter = event.submitter;
    const isCheckout =
      !submitter ||
      (submitter instanceof HTMLElement &&
        (submitter.getAttribute('name') === 'checkout' ||
          submitter.id === 'checkout' ||
          submitter.classList.contains('cart__checkout-button')));

    if (!isCheckout) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    void this.#openForCheckout();
  };

  async #openForCheckout() {
    this.#busy = true;
    this.#setLoading(true);
    await this.#closeCartDrawer();
    this.#showDialog();

    try {
      const eligible = await this.#eligibleCharms();
      if (eligible.length === 0) {
        this.#goToCheckout();
        return;
      }

      this.#syncCardVisibility(eligible);
      this.#setLoading(false);
      this.#startTimer();
      this.#burstConfetti();
    } catch (error) {
      console.warn('[charms-upsell] open failed, going to checkout', error);
      this.#goToCheckout();
    } finally {
      this.#busy = false;
    }
  }

  async #closeCartDrawer() {
    const drawer = /** @type {HTMLElement & { close?: () => Promise<void> | void } | null} */ (
      document.getElementById('cart-drawer')
    );
    if (!drawer) return;

    try {
      const result = drawer.close?.();
      if (result && typeof result.then === 'function') await result;
    } catch {
      // ignore
    }

    const panel = drawer.querySelector('dialog');
    if (panel instanceof HTMLDialogElement && panel.open) {
      try {
        panel.close();
      } catch {
        panel.removeAttribute('open');
      }
    }

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  #showDialog() {
    if (!this.#dialog) {
      this.#goToCheckout();
      return;
    }

    this.removeAttribute('hidden');

    try {
      if (typeof this.#dialog.showModal === 'function') {
        this.#dialog.showModal();
      } else {
        this.#dialog.setAttribute('open', '');
      }
    } catch (error) {
      console.warn('[charms-upsell] showModal failed', error);
      this.#dialog.setAttribute('open', '');
      this.#dialog.style.display = 'block';
    }
  }

  /**
   * @param {{product_type?:string,product_title?:string,title?:string}} item
   */
  #isBagItem(item) {
    const type = String(item.product_type || '').toLowerCase();
    const title = String(item.product_title || item.title || '').toLowerCase();
    if (type.includes('charm') || title.includes('charm')) return false;
    if (type.includes('bag') || type.includes('tote') || type.includes('clutch')) return true;
    return (
      title.includes('bag') ||
      title.includes('tote') ||
      title.includes('clutch') ||
      title.includes('hobo') ||
      title.includes('bucket')
    );
  }

  /** @returns {Promise<CharmOffer[]>} */
  async #eligibleCharms() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ELIGIBILITY_TIMEOUT_MS);

    try {
      const cartUrl = this.dataset.cartUrl || '/cart.js';
      const response = await fetch(cartUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!response.ok) return this.#charms;

      const cart = await response.json();
      const items = cart.items || [];
      const hasBag = items.some((/** @type {{product_type?:string,title?:string}} */ item) => this.#isBagItem(item));
      if (!hasBag) return [];

      const inCart = new Set(
        items.map((/** @type {{variant_id?:number,id?:number}} */ item) => Number(item.variant_id || item.id))
      );
      return this.#charms.filter((charm) => !inCart.has(Number(charm.variantId)));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return [];
      return this.#charms;
    } finally {
      clearTimeout(timer);
    }
  }

  /** @param {CharmOffer[]} eligible */
  #syncCardVisibility(eligible) {
    const eligibleIds = new Set(eligible.map((c) => String(c.variantId)));
    this.querySelectorAll('.charms-upsell__card').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      card.hidden = !eligibleIds.has(String(card.dataset.variantId));
    });
  }

  /**
   * @param {CharmOffer} charm
   * @param {HTMLButtonElement} button
   */
  async #onAdd(charm, button) {
    if (this.#busy || this.#proceeding) return;
    this.#busy = true;

    const label = button.querySelector('[data-card-add-label]');
    const previous = label?.textContent || this.#labels.add;
    button.disabled = true;
    if (label) label.textContent = this.#labels.adding;

    try {
      const addUrl = this.dataset.cartAddUrl || '/cart/add.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: charm.variantId, quantity: 1 }],
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status) {
        const message = String(payload.description || payload.message || 'Could not add charm');
        if (/sold out|not available|inventory|out of stock/i.test(message)) {
          this.#removeCharm(charm.variantId);
          this.#showToast('That charm is out of stock');
        }
        throw new Error(message);
      }

      if (label) label.textContent = this.#labels.added;
      button.classList.add('is-added');
      const card = button.closest('.charms-upsell__card');
      if (card instanceof HTMLElement) card.dataset.added = 'true';
      this.#burstConfetti();
      this.#showToast('Charm added at 50% off');
    } catch (error) {
      console.warn('[charms-upsell]', error);
      button.disabled = false;
      if (label) label.textContent = previous;
    } finally {
      this.#busy = false;
    }
  }

  #onSkip = () => {
    if (this.querySelector('.charms-upsell__add[disabled]:not(.is-added)')) return;
    this.#goToCheckout();
  };

  /** @param {Event} event */
  #onDialogCancel = (event) => {
    event.preventDefault();
    this.#onSkip();
  };

  /** @param {MouseEvent} event */
  #onBackdropClick = (event) => {
    if (event.target === this.#dialog) this.#onSkip();
  };

  #goToCheckout() {
    if (this.#proceeding) return;
    this.#proceeding = true;
    this.#stopTimer();

    try {
      this.#dialog?.close();
    } catch {
      this.#dialog?.removeAttribute('open');
    }

    const checkoutUrl = this.dataset.checkoutUrl || '/checkout';
    const sync = window.kbSyncCharmBagDiscount;
    const go = () => window.location.assign(checkoutUrl);
    if (typeof sync === 'function') {
      void Promise.resolve(sync()).finally(go);
      return;
    }
    go();
  }

  #setLoading(isLoading) {
    const loading = this.querySelector('[data-upsell-loading]');
    const panel = this.querySelector('.charms-upsell__panel');
    if (loading instanceof HTMLElement) loading.hidden = !isLoading;
    if (panel instanceof HTMLElement) panel.classList.toggle('is-loading', isLoading);
  }

  #offerSeconds() {
    const parsed = Number(this.dataset.offerSeconds);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 300;
  }

  #startTimer() {
    const timer = this.querySelector('[data-upsell-timer]');
    const value = this.querySelector('[data-upsell-timer-value]');
    if (!(timer instanceof HTMLElement) || !(value instanceof HTMLElement)) return;

    timer.hidden = false;
    const durationMs = this.#offerSeconds() * 1000;
    let endsAt = Number(sessionStorage.getItem(OFFER_STORAGE_KEY) || 0);
    const now = Date.now();
    if (!endsAt || endsAt < now) {
      endsAt = now + durationMs;
      sessionStorage.setItem(OFFER_STORAGE_KEY, String(endsAt));
    }

    const tick = () => {
      const remaining = Math.max(0, endsAt - Date.now());
      const totalSeconds = Math.ceil(remaining / 1000);
      const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
      const seconds = String(totalSeconds % 60).padStart(2, '0');
      value.textContent = `${minutes}:${seconds}`;
      timer.classList.toggle('is-urgent', totalSeconds <= 30);
      if (remaining <= 0) this.#stopTimer();
    };

    this.#stopTimer();
    tick();
    this.#timerId = setInterval(tick, 250);
  }

  #stopTimer() {
    if (this.#timerId) {
      clearInterval(this.#timerId);
      this.#timerId = null;
    }
  }

  /** @param {number} variantId */
  #removeCharm(variantId) {
    this.#charms = this.#charms.filter((charm) => Number(charm.variantId) !== Number(variantId));
    this.querySelectorAll('.charms-upsell__card').forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      if (String(card.dataset.variantId) === String(variantId)) card.remove();
    });
  }

  #showToast(message) {
    const toast = this.querySelector('[data-upsell-toast]');
    if (!(toast instanceof HTMLElement)) return;
    toast.textContent = message;
    toast.hidden = false;
    toast.classList.remove('is-in');
    void toast.offsetWidth;
    toast.classList.add('is-in');
    window.setTimeout(() => {
      toast.classList.remove('is-in');
      toast.hidden = true;
    }, 2200);
  }

  #burstConfetti() {
    const root = this.querySelector('[data-upsell-confetti]');
    if (!(root instanceof HTMLElement)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    root.replaceChildren();
    const colors = ['#b42318', '#ffffff', '#f4c518', '#1c1917', '#e8b4b8'];
    for (let i = 0; i < 42; i += 1) {
      const piece = document.createElement('span');
      piece.className = 'charms-upsell__confetti-piece';
      piece.style.setProperty('--x', `${Math.random() * 100}%`);
      piece.style.setProperty('--delay', `${Math.random() * 180}ms`);
      piece.style.setProperty('--rot', `${Math.random() * 360}deg`);
      piece.style.background = colors[i % colors.length];
      root.appendChild(piece);
    }

    window.setTimeout(() => {
      root.replaceChildren();
    }, 1400);
  }
}

if (!customElements.get('charms-checkout-upsell')) {
  customElements.define('charms-checkout-upsell', CharmsCheckoutUpsell);
}
